import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  clientModelsService,
  ClientModel3D,
  BuildingParameters,
  Model3DGeometry,
} from "../../services/client-models.service";
import { ClientViewport3D } from "../../components/3d/ClientViewport3D";

const DEFAULT_PARAMS: BuildingParameters = {
  width: 10,
  depth: 8,
  height: 3,
  floors: 1,
  roofType: "flat",
  wallMaterial: "brick",
  windowCount: 4,
  doorCount: 1,
  finishQuality: "standard",
};

// Réplica local de la lógica del Worker para vista previa en vivo
function generateGeometryLocal(params: BuildingParameters): Model3DGeometry {
  const { width, depth, height, floors } = params;
  const totalHeight = height * floors;
  const wallThickness = 0.2;
  const halfW = width / 2;
  const halfD = depth / 2;

  const walls: Model3DGeometry["walls"] = [];
  for (let floor = 0; floor < floors; floor++) {
    const baseY = floor * height;
    const yMid = baseY + height / 2;
    walls.push({ id: `wall-front-${floor}`, x: 0, y: yMid, z: -halfD, width, height, depth: wallThickness, orientation: "front" });
    walls.push({ id: `wall-back-${floor}`, x: 0, y: yMid, z: halfD, width, height, depth: wallThickness, orientation: "back" });
    walls.push({ id: `wall-left-${floor}`, x: -halfW, y: yMid, z: 0, width: wallThickness, height, depth, orientation: "left" });
    walls.push({ id: `wall-right-${floor}`, x: halfW, y: yMid, z: 0, width: wallThickness, height, depth, orientation: "right" });
  }

  const windows: Model3DGeometry["windows"] = [];
  const winWidth = 1.2, winHeight = 1.4;
  const winSpacing = width / (params.windowCount + 1);
  for (let i = 0; i < params.windowCount; i++) {
    windows.push({
      id: `window-${i}`,
      x: -halfW + winSpacing * (i + 1),
      y: 1.6,
      z: -halfD - 0.05,
      width: winWidth,
      height: winHeight,
      wall: "front",
    });
  }

  const doors: Model3DGeometry["doors"] = [];
  const doorWidth = 1.0, doorHeight = 2.1;
  for (let i = 0; i < params.doorCount; i++) {
    doors.push({
      id: `door-${i}`,
      x: -halfW + (width / (params.doorCount + 1)) * (i + 1),
      y: doorHeight / 2,
      z: -halfD - 0.05,
      width: doorWidth,
      height: doorHeight,
      wall: "front",
    });
  }

  const roofHeight = params.roofType === "pitched" ? 1.5 : 0.2;

  const wallColor = params.wallMaterial === "concrete" ? "#d4d4d4"
    : params.wallMaterial === "wood" ? "#c4a882"
    : params.wallMaterial === "steel" ? "#9ca3af"
    : "#e8e0d8";

  return {
    version: 1,
    dimensions: { width, depth, height, totalHeight, floors },
    floor: { width, depth, y: -0.05 },
    walls,
    roof: {
      type: params.roofType,
      width: width + 0.4,
      depth: depth + 0.4,
      y: totalHeight + roofHeight / 2,
      height: roofHeight,
    },
    windows,
    doors,
    materials: {
      wall: wallColor,
      floor: "#cccccc",
      roof: params.roofType === "green" ? "#4a7a4a" : params.roofType === "pitched" ? "#8b4513" : "#8B7355",
      window: "#88ccff",
      door: "#6B4226",
    },
  };
}

export function ModelEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [model, setModel] = useState<ClientModel3D | null>(null);
  const [params, setParams] = useState<BuildingParameters>(DEFAULT_PARAMS);
  const [localGeometry, setLocalGeometry] = useState<Model3DGeometry | null>(null);
  const [wireframe, setWireframe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setIsLoading(true);
      try {
        const data = await clientModelsService.get(id);
        setModel(data);
        if (data.parameters) setParams(data.parameters);
        if (data.geometry) setLocalGeometry(data.geometry);
      } catch (err: any) {
        setError(err?.message || "Error al cargar modelo");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id]);

  // Actualizar geometría local cuando cambian los parámetros
  useEffect(() => {
    setLocalGeometry(generateGeometryLocal(params));
  }, [params]);

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await clientModelsService.update(id, { parameters: params });
      setModel(updated);
      setSuccess("Modelo guardado");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.message || "Error al guardar");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkReady = async () => {
    if (!id) return;
    if (!confirm("¿Marcar este modelo como listo para cotizar? El equipo del despacho lo revisará.")) return;
    setIsMarkingReady(true);
    setError(null);
    try {
      await clientModelsService.update(id, { parameters: params });
      const updated = await clientModelsService.markReady(id);
      setModel(updated);
      setSuccess("¡Modelo marcado como listo para cotizar!");
    } catch (err: any) {
      setError(err?.message || "Error al marcar como listo");
    } finally {
      setIsMarkingReady(false);
    }
  };

  const updateParam = <K extends keyof BuildingParameters>(key: K, value: BuildingParameters[K]) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!model) {
    return (
      <div className="text-center py-16">
        <div className="text-4xl mb-3">😕</div>
        <p className="text-slate-600">Modelo no encontrado</p>
        <button
          onClick={() => navigate("/dashboard/models")}
          className="mt-4 text-primary hover:underline font-medium"
        >
          ← Volver a mis modelos
        </button>
      </div>
    );
  }

  const isReady = model.status === "ready_for_quote" || model.status === "quoted";
  const readOnly = isReady;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard/models")}
            className="text-sm text-slate-500 hover:text-primary"
          >
            ← Volver
          </button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{model.name}</h2>
            {model.generationInput && (
              <p className="text-xs text-slate-500 italic mt-0.5">"{model.generationInput}"</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isReady ? (
            <span className="text-sm font-semibold text-green-700 bg-green-100 px-3 py-1.5 rounded-full">
              ✅ Listo para cotizar
            </span>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
              >
                {isSaving ? "Guardando..." : "💾 Guardar"}
              </button>
              <button
                onClick={handleMarkReady}
                disabled={isMarkingReady}
                className="text-sm bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
              >
                {isMarkingReady ? "Marcando..." : "✅ Listo para cotizar"}
              </button>
            </>
          )}
        </div>
      </div>

      {(error || success) && (
        <div
          className={`rounded-lg p-3 text-sm ${
            error
              ? "bg-red-50 border border-red-200 text-red-700"
              : "bg-green-50 border border-green-200 text-green-700"
          }`}
        >
          {error || success}
        </div>
      )}

      {/* Layout 2 columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Visor 3D */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-sm">Vista previa 3D</h3>
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
              <input
                type="checkbox"
                checked={wireframe}
                onChange={(e) => setWireframe(e.target.checked)}
                className="w-3 h-3 accent-primary"
              />
              Modo alambre
            </label>
          </div>
          {localGeometry && (
            <ClientViewport3D geometry={localGeometry} wireframe={wireframe} height={520} />
          )}
          <p className="text-xs text-slate-400 mt-2 text-center">
            Arrastra para rotar • Scroll para acercar • Click derecho para desplazar
          </p>
        </div>

        {/* Panel de parámetros */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 max-h-[700px] overflow-y-auto">
          <h3 className="font-semibold text-slate-800 text-sm">
            Parámetros {readOnly && <span className="text-slate-400 font-normal">(solo lectura)</span>}
          </h3>

          {/* Dimensiones */}
          <ParamRow label="Ancho (m)">
            <NumberInput
              value={params.width}
              min={3} max={50} step={0.5}
              disabled={readOnly}
              onChange={(v) => updateParam("width", v)}
            />
          </ParamRow>
          <ParamRow label="Profundidad (m)">
            <NumberInput
              value={params.depth}
              min={3} max={50} step={0.5}
              disabled={readOnly}
              onChange={(v) => updateParam("depth", v)}
            />
          </ParamRow>
          <ParamRow label="Altura por piso (m)">
            <NumberInput
              value={params.height}
              min={2} max={6} step={0.1}
              disabled={readOnly}
              onChange={(v) => updateParam("height", v)}
            />
          </ParamRow>
          <ParamRow label="Pisos">
            <NumberInput
              value={params.floors}
              min={1} max={5} step={1}
              disabled={readOnly}
              onChange={(v) => updateParam("floors", Math.floor(v))}
            />
          </ParamRow>

          {/* Techo */}
          <ParamRow label="Tipo de techo">
            <select
              value={params.roofType}
              disabled={readOnly}
              onChange={(e) => updateParam("roofType", e.target.value as any)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-primary outline-none disabled:opacity-60"
            >
              <option value="flat">Plano</option>
              <option value="pitched">Inclinado</option>
              <option value="green">Verde</option>
            </select>
          </ParamRow>

          {/* Material */}
          <ParamRow label="Material de muros">
            <select
              value={params.wallMaterial}
              disabled={readOnly}
              onChange={(e) => updateParam("wallMaterial", e.target.value as any)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-primary outline-none disabled:opacity-60"
            >
              <option value="brick">Ladrillo / Block</option>
              <option value="concrete">Concreto</option>
              <option value="wood">Madera</option>
              <option value="steel">Acero</option>
            </select>
          </ParamRow>

          {/* Ventanas y puertas */}
          <ParamRow label="Ventanas al frente">
            <NumberInput
              value={params.windowCount}
              min={0} max={20} step={1}
              disabled={readOnly}
              onChange={(v) => updateParam("windowCount", Math.floor(v))}
            />
          </ParamRow>
          <ParamRow label="Puertas al frente">
            <NumberInput
              value={params.doorCount}
              min={0} max={6} step={1}
              disabled={readOnly}
              onChange={(v) => updateParam("doorCount", Math.floor(v))}
            />
          </ParamRow>

          {/* Calidad */}
          <ParamRow label="Calidad de acabados">
            <select
              value={params.finishQuality}
              disabled={readOnly}
              onChange={(e) => updateParam("finishQuality", e.target.value as any)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-primary outline-none disabled:opacity-60"
            >
              <option value="basic">Básica</option>
              <option value="standard">Estándar</option>
              <option value="premium">Premium</option>
              <option value="luxury">Lujo</option>
            </select>
          </ParamRow>

          {!readOnly && (
            <div className="pt-2 border-t border-slate-100">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-lg font-medium text-sm transition disabled:opacity-50"
              >
                {isSaving ? "Guardando..." : "💾 Guardar cambios"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ParamRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function NumberInput({
  value, min, max, step, disabled, onChange,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onChange(Math.min(Math.max(v, min), max));
      }}
      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-primary outline-none disabled:opacity-60"
    />
  );
}